(() => {
  const HOLD_AT = 1.2;
  const PLAYBACK_RATE = 0.7;
  const MID_TOLERANCE = 0.18;
  const DESKTOP = window.matchMedia('(min-width: 990px)');

  const pageWrapper = () => document.querySelector('.page-wrapper');

  class WaterStoryLogo extends HTMLElement {
    #video = null;
    #ready = false;
    #armed = true;
    #playing = false;
    #held = false;
    #observer = null;
    #raf = 0;
    #midRaf = 0;
    #holdTimer = 0;

    #onBreakpoint = () => {
      this.#bindObserver();
    };

    #onEnded = () => {
      this.#freezeAtHold();
    };

    connectedCallback() {
      this.#video = this.querySelector('video');
      if (!this.#video) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      const video = this.#video;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = false;
      video.removeAttribute('loop');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.preload = 'none';
      video.disableRemotePlayback = true;

      const arm = () => {
        if (this.#ready) return;
        this.#ensureSrc();
        if (!video.getAttribute('src')) return;
        if (video.readyState < 1) return;
        this.#ready = true;
        this.#showFirstFrame();
        this.#ensureMidWatch();
      };

      video.addEventListener('loadedmetadata', arm, { once: true });
      video.addEventListener('loadeddata', arm, { once: true });
      video.addEventListener('canplay', arm, { once: true });
      video.addEventListener('ended', this.#onEnded);
      video.addEventListener('error', () => {
        this.classList.remove('is-active');
      });

      if (video.readyState >= 1) arm();

      DESKTOP.addEventListener('change', this.#onBreakpoint);
      window.addEventListener('kb:motion-ready', this.#onBreakpoint);
      this.#bindObserver();
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
      this.#observer = null;
      this.#stopMidWatch();
      this.#stopHoldWatch();
      this.#video?.removeEventListener('ended', this.#onEnded);
      DESKTOP.removeEventListener('change', this.#onBreakpoint);
      window.removeEventListener('kb:motion-ready', this.#onBreakpoint);
    }

    #bindObserver() {
      this.#observer?.disconnect();

      this.#observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;

          if (!entry.isIntersecting) {
            this.#onFullyOut();
            return;
          }

          this.#ensureSrc();
          this.#ensureMidWatch();
        },
        {
          root: DESKTOP.matches ? pageWrapper() : null,
          threshold: [0, 0.01, 0.1],
        }
      );
      this.#observer.observe(this.closest('.water-story') || this);
    }

    #ensureSrc() {
      const video = this.#video;
      if (!video || video.getAttribute('src')) return;
      const src = video.dataset.src;
      if (!src) return;
      video.src = src;
      video.preload = 'metadata';
      try {
        video.load();
      } catch {}
    }

    #showFirstFrame() {
      const video = this.#video;
      if (!video || !this.#ready) return;

      try {
        video.pause();
      } catch {}

      const reveal = () => {
        this.classList.add('is-active');
      };

      if (video.currentTime > 0.02) {
        const onSeeked = () => {
          video.removeEventListener('seeked', onSeeked);
          reveal();
        };
        video.addEventListener('seeked', onSeeked);
        try {
          video.currentTime = 0;
        } catch {
          reveal();
        }
      } else {
        try {
          video.currentTime = 0;
        } catch {}
        reveal();
      }
    }

    #onFullyOut() {
      this.#stopMidWatch();
      this.#stopHoldWatch();
      this.#playing = false;
      this.#held = false;
      this.#armed = true;
      this.#showFirstFrame();
    }

    #ensureMidWatch() {
      if (this.#midRaf || !this.#armed || this.#playing) return;

      const tick = () => {
        this.#midRaf = 0;
        if (!this.#armed || this.#playing) return;

        if (this.#isNearViewportMiddle()) {
          this.#playOnce();
          return;
        }

        this.#midRaf = requestAnimationFrame(tick);
      };

      this.#midRaf = requestAnimationFrame(tick);
    }

    #stopMidWatch() {
      if (this.#midRaf) {
        cancelAnimationFrame(this.#midRaf);
        this.#midRaf = 0;
      }
    }

    #isNearViewportMiddle() {
      const host = this.closest('.water-story') || this;
      const root = DESKTOP.matches ? pageWrapper() : null;
      const vh = root?.clientHeight || window.innerHeight || 1;
      const rootTop = root?.getBoundingClientRect().top ?? 0;
      const rect = host.getBoundingClientRect();
      const center = rect.top - rootTop + rect.height / 2;
      const mid = vh / 2;
      return Math.abs(center - mid) <= vh * MID_TOLERANCE;
    }

    #playOnce() {
      this.#ensureSrc();
      const video = this.#video;
      if (!video || !this.#ready || !this.#armed || this.#playing) return;

      this.#armed = false;
      this.#playing = true;
      this.#held = false;
      this.#stopMidWatch();
      this.classList.add('is-active');
      video.playbackRate = PLAYBACK_RATE;

      if (video.preload !== 'auto') {
        video.preload = 'auto';
      }

      const start = () => {
        this.#watchHold();
        const playAttempt = video.play();
        if (playAttempt && typeof playAttempt.then === 'function') {
          playAttempt.catch(() => this.#freezeAtHold());
        }
      };

      if (video.readyState < 2) {
        const onReady = () => {
          video.removeEventListener('canplay', onReady);
          start();
        };
        video.addEventListener('canplay', onReady);
        try {
          video.load();
        } catch {
          start();
        }
        return;
      }

      if (video.currentTime > 0.05) {
        const onSeeked = () => {
          video.removeEventListener('seeked', onSeeked);
          start();
        };
        video.addEventListener('seeked', onSeeked);
        try {
          video.currentTime = 0;
        } catch {
          start();
        }
      } else {
        start();
      }
    }

    #watchHold() {
      this.#stopHoldWatch();

      const tick = () => {
        const video = this.#video;
        if (!video || this.#held) return;
        if (video.currentTime >= HOLD_AT) {
          this.#freezeAtHold();
          return;
        }
        this.#raf = requestAnimationFrame(tick);
      };
      this.#raf = requestAnimationFrame(tick);

      this.#holdTimer = window.setTimeout(
        () => this.#freezeAtHold(),
        (HOLD_AT / PLAYBACK_RATE) * 1000 + 200
      );
    }

    #stopHoldWatch() {
      if (this.#raf) {
        cancelAnimationFrame(this.#raf);
        this.#raf = 0;
      }
      if (this.#holdTimer) {
        clearTimeout(this.#holdTimer);
        this.#holdTimer = 0;
      }
    }

    #freezeAtHold() {
      const video = this.#video;
      if (!video || this.#held) return;
      this.#held = true;
      this.#playing = false;
      this.#stopHoldWatch();

      try {
        video.pause();
      } catch {}

      const pin = () => {
        try {
          if (Math.abs(video.currentTime - HOLD_AT) > 0.05) {
            video.currentTime = HOLD_AT;
          }
        } catch {}
      };

      pin();
      video.addEventListener('seeked', pin, { once: true });
      this.classList.add('is-active');
    }
  }

  if (!customElements.get('water-story-scrub')) {
    customElements.define('water-story-scrub', WaterStoryLogo);
  }
})();
